import type { SubEntryType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  AddImageButton,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEventHandler } from "react"
import {
  useCallback,
  useState,
} from "react"

import { useUploadImage } from "src/hooks"
import { UploadImageErrorToast } from "src/toasts"
import type { EditableSubEntry } from "src/types"
import {
  getSugarmateError,
  injectProps,
} from "src/utils"

interface Props<T extends SubEntryType> {
  updateSubEntry: (s: Partial<EditableSubEntry<T>>) => void;
  required?: boolean;
}

export function UploadImage<T extends SubEntryType>(props: Props<T>) {
  const {
    updateSubEntry,
    required,
  } = props

  const [
    disabled,
    setDisabled,
  ] = useState<boolean>(false)

  const { showToast } = useToasterContext()
  const uploadImage = useUploadImage()

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (ev) => {
      const file = ev.target.files?.[0]

      if (!file) {
        return
      }

      void (async () => {
        setDisabled(true)

        try {
          if (!uploadImage) {
            throw new Error("cannot upload image")
          }

          const response = await uploadImage(file)

          updateSubEntry({
            image_id: response.data.image_id,
            image_url: response.data.image_url,
            thumbnail: response.data.thumbnail,
          })
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            UploadImageErrorToast,
            {
              code: sugarmateError?.code,
              status: sugarmateError?.status,
            },
          )

          showToast(
            `upload-image-error-toast-${file.name}`,
            ErrorToast,
          )
        }

        setDisabled(false)
      })()
    },
    [
      uploadImage,
      updateSubEntry,
      showToast,
    ],
  )

  return (
    <AddImageButton
      htmlFor="upload_image"
      onChange={onChange}
      disabled={disabled || !uploadImage}
      required={required}
      id="upload-image"
    />
  )
}
